import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchProjectTeam, fetchStaffDirectory, type StaffOption } from '../../utils/projectTeam';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';

/**
 * Who at Portal is responsible for this project. The assignments live in
 * public.project_staff — internal only, and entirely separate from the client
 * access system — so they survive leaving, refreshing, signing out and opening
 * Portal anywhere else.
 */
export function PortalTeam() {
  const {
    activeProject,
    setProjectTeam,
    addTeamMember,
    updateTeamMember,
    removeTeamMember
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const projectId = activeProject.id;
  const slug = activeProject.slug;

  /* The saved assignments, read every time the project is opened. Whatever the
     session held is replaced by exactly what project_staff contains. */
  useEffect(() => {
    let live = true;
    void fetchProjectTeam(projectId, slug).then(team => {
      if (live) setProjectTeam(team);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, slug]);
  useEffect(() => {
    let live = true;
    void fetchStaffDirectory().then(people => {
      if (live) setStaff(people);
    });
    return () => {
      live = false;
    };
  }, []);
  const assignedIds = new Set(activeProject.team.map(member => member.id));
  const available = staff.filter(person => !assignedIds.has(person.userId));
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const person = available.find(candidate => candidate.userId === userId) ?? available[0];
    if (!person) return;
    /* No confirmation here — the assignment is only real once saved. */
    addTeamMember({
      userId: person.userId,
      name: person.name,
      role: role.trim() || 'Crew'
    });
    setUserId('');
    setRole('');
    setOpen(false);
  }
  return <section aria-labelledby="portal-team" className="mt-16" data-id="element-260" data-mp="3a7m9fnfjxxvl">
      <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-261" data-mp="357q5fo6uu6g1">
        <h2 id="portal-team" className="label m-0 text-paper/40" data-id="element-262" data-mp="5s318rwhjowk">
          Portal Team
        </h2>
        <p className="label-sm m-0 text-paper/35" data-id="element-263" data-mp="1q7ydiywp3go">Responsibility only · internal</p>
      </div>

      <ul className="m-0 list-none p-0" data-id="element-264" data-mp="3swslirlubr8f">
        {activeProject.team.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-265" data-mp="3nxy0rk4bbslo">
            No team assigned yet.
          </li>}
        {activeProject.team.map(member => <li key={member.id} className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 border-b border-ruleDark py-6" data-id="element-266" data-mp="2cisoboedqfov">
            <p className="min-w-0 flex-1 font-display text-2xl leading-none" data-id="element-267" data-mp="22goyzwkq4h9q">{member.name}</p>
            <label className="min-w-[12rem]" data-id="element-268" data-mp="27e8e0yp8wy2q">
              <span className="sr-only" data-id="element-269" data-mp="2cci979ayzyoc">Role for {member.name}</span>
              <input value={member.role} onChange={event => updateTeamMember(member.id, {
            role: event.target.value
          })} className={`${FIELD} mt-0`} data-id="element-270" data-mp="2cb75talfssxr" />
            </label>
            <button type="button" onClick={() => removeTeamMember(member.id)} className={BUTTON_QUIET} data-id="element-271" data-mp="2q3x3wde8pob8">
              Remove
            </button>
          </li>)}
      </ul>

      {open ? <form onSubmit={submit} className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-end" data-id="element-272" data-mp="301up0s0sz4zm">
          <label className="flex-1" data-id="element-273" data-mp="10rcsomc5yr9s">
            <span className="label block text-paper/40" data-id="element-274" data-mp="15obln1k3cs6e">Staff Member</span>
            {available.length ? <select value={userId || available[0].userId} onChange={event => setUserId(event.target.value)} autoFocus className={SELECT} data-id="element-275" data-mp="2w574m1px0nq3">
                {available.map(person => <option key={person.userId} value={person.userId} className={OPTION} data-id="element-276" data-mp="3540yhkbv2zzy">
                    {person.name}
                  </option>)}
              </select> : <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-277" data-mp="28g4h4d8lo0no">
                {staff.length ? 'Everyone is already assigned' : 'No Portal staff available'}
              </p>}
          </label>
          <label className="flex-1" data-id="element-278" data-mp="2hg10bzc807bc">
            <span className="label block text-paper/40" data-id="element-279" data-mp="2hezovtralit3">Role</span>
            <input value={role} onChange={event => setRole(event.target.value)} placeholder="e.g. Project Lead" className={FIELD} data-id="element-280" data-mp="1tpng2uzvncrb" />
          </label>
          <div className="flex items-center gap-6" data-id="element-281" data-mp="47zzjnpvwx8s">
            <button type="submit" disabled={!available.length} className={BUTTON_SOLID} data-id="element-282" data-mp="91urtfjk2kjh">
              Assign
            </button>
            <button type="button" onClick={() => setOpen(false)} className={BUTTON_QUIET} data-id="element-283" data-mp="3rgn8eokl6jbx">
              Cancel
            </button>
          </div>
        </form> : <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-284" data-mp="nz51qkgjvid3">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-285" data-mp="e05zpkqaikz2" />
          Assign Team Member
        </button>}
    </section>;
}